import {
  User, FileText, Briefcase, GraduationCap, CheckSquare, FolderGit
} from 'lucide-react';

export const ESSENTIAL_SECTIONS = [
  {
    id: "contact",
    title: "Contact Info",
    icon: User,
    category: "essentials",
    allowMultiple: false,
    defaultVisible: true,
    emptyState: "No contact info available.",
    editorComponent: "ContactEditor",
    rendererComponent: "ContactRenderer",
    layout: {
      preferredColumn: "main",
      priority: 100,
      collapsible: true,
      allowPageBreak: false,
      keepTogether: true,
    },
    capabilities: {
      aiRewrite: false,
      reorder: false,
      duplicate: false,
      deletable: false,
      hideable: false,
      exportable: true,
    },
    recommendedFor: ["all"],
    badges: ["ATS Essential", "Required"],
    fields: [
      { name: "name", label: "Full Name", type: "text", required: true, placeholder: "John Doe" },
      { name: "role", label: "Professional Title", type: "text", required: false, placeholder: "Software Engineer" },
      { name: "email", label: "Email Address", type: "email", required: true, placeholder: "john@example.com" },
      { name: "phone", label: "Phone Number", type: "phone", required: true, placeholder: "+1 (555) 019-2834" },
      { name: "city", label: "Location", type: "text", required: false, placeholder: "San Francisco, CA" },
    ]
  },
  {
    id: "summary",
    title: "Professional Summary",
    icon: FileText,
    category: "essentials",
    allowMultiple: false,
    defaultVisible: true,
    emptyState: "No summary text added.",
    editorComponent: "SummaryEditor",
    rendererComponent: "SummaryRenderer",
    layout: {
      preferredColumn: "main",
      priority: 90,
      collapsible: true,
      allowPageBreak: true,
      keepTogether: false,
    },
    capabilities: {
      aiRewrite: true,
      reorder: false,
      duplicate: false,
      deletable: false,
      hideable: true,
      exportable: true,
    },
    recommendedFor: ["all"],
    badges: ["ATS Essential", "Recommended"],
    fields: [
      { name: "summary", label: "Summary Text", type: "textarea", required: true, placeholder: "Experienced engineer targetting..." }
    ]
  },
  {
    id: "experience",
    title: "Work Experience",
    icon: Briefcase,
    category: "essentials",
    allowMultiple: true,
    defaultVisible: true,
    emptyState: "No work experience added yet.",
    editorComponent: "ExperienceEditor",
    rendererComponent: "ExperienceRenderer",
    layout: {
      preferredColumn: "main",
      priority: 80,
      collapsible: true,
      allowPageBreak: true,
      keepTogether: false,
    },
    capabilities: {
      aiRewrite: true,
      reorder: true,
      duplicate: true,
      deletable: true,
      hideable: true,
      exportable: true,
    },
    recommendedFor: ["all"],
    badges: ["ATS Essential"],
    fields: [
      { name: "company", label: "Company / Organization", type: "text", required: true, placeholder: "Google" },
      { name: "role", label: "Role / Job Title", type: "text", required: true, placeholder: "Software Engineer" },
      { name: "duration", label: "Duration / Dates", type: "text", required: false, placeholder: "Jan 2022 - Present" },
      { name: "location", label: "Location", type: "text", required: false, placeholder: "Mountain View, CA" },
      { name: "points", label: "Bullet Points", type: "tag", required: false, placeholder: "Accomplished..." }
    ],
    defaultData: {
      company: "",
      role: "",
      duration: "",
      location: "",
      points: [""]
    }
  },
  {
    id: "education",
    title: "Education",
    icon: GraduationCap,
    category: "essentials",
    allowMultiple: true,
    defaultVisible: true,
    emptyState: "No education entries added yet.",
    editorComponent: "EducationEditor",
    rendererComponent: "EducationRenderer",
    layout: {
      preferredColumn: "main",
      priority: 70,
      collapsible: true,
      allowPageBreak: true,
      keepTogether: false,
    },
    capabilities: {
      aiRewrite: false,
      reorder: true,
      duplicate: true,
      deletable: true,
      hideable: true,
      exportable: true,
    },
    recommendedFor: ["all"],
    badges: ["ATS Essential"],
    fields: [
      { name: "college", label: "College / University", type: "text", required: true, placeholder: "Stanford University" },
      { name: "degree", label: "Degree / Program", type: "text", required: true, placeholder: "M.S. in Computer Science" },
      { name: "year", label: "Graduation Year / Dates", type: "text", required: false, placeholder: "2024" },
      { name: "cgpa", label: "Grade / CGPA", type: "text", required: false, placeholder: "3.9/4.0" }
    ],
    defaultData: {
      college: "",
      degree: "",
      year: "",
      cgpa: ""
    }
  },
  {
    id: "skills",
    title: "Skills",
    icon: CheckSquare,
    category: "essentials",
    allowMultiple: true,
    defaultVisible: true,
    emptyState: "No skills listed yet.",
    editorComponent: "SkillsEditor",
    rendererComponent: "SkillsRenderer",
    layout: {
      preferredColumn: "main",
      priority: 60,
      collapsible: true,
      allowPageBreak: false,
      keepTogether: true,
    },
    capabilities: {
      aiRewrite: true,
      reorder: false,
      duplicate: false,
      deletable: false,
      hideable: true,
      exportable: true,
    },
    recommendedFor: ["all"],
    badges: ["ATS Essential"],
    fields: [
      { name: "skills", label: "Skills Tags", type: "tag", required: true, placeholder: "React, Docker, Node.js..." }
    ]
  },
  {
    id: "projects",
    title: "Projects",
    icon: FolderGit,
    category: "essentials",
    allowMultiple: true,
    defaultVisible: true,
    emptyState: "No projects listed yet.",
    editorComponent: "ProjectEditor",
    rendererComponent: "ProjectRenderer",
    layout: {
      preferredColumn: "main",
      priority: 50,
      collapsible: true,
      allowPageBreak: true,
      keepTogether: false,
    },
    capabilities: {
      aiRewrite: true,
      reorder: true,
      duplicate: true,
      deletable: true,
      hideable: true,
      exportable: true,
    },
    recommendedFor: ["all"],
    badges: ["Recommended"],
    fields: [
      { name: "name", label: "Project Title", type: "text", required: true, placeholder: "E-Commerce API" },
      { name: "description", label: "Short Description", type: "text", required: false, placeholder: "A RESTful microservice..." },
      { name: "points", label: "Accomplishments", type: "tag", required: false, placeholder: "Built with Node.js..." }
    ],
    defaultData: {
      name: "",
      description: "",
      points: [""]
    }
  }
];
